// SPDX-License-Identifier: Apache-2.0

// EditHistoryViewer — surfaces the persistent Yjs snapshot log for the
// active doc.
//
// Layout: a 220px-wide list of snapshot rows on the left, a read-only
// `<tether-doc>` preview on the right showing the historical state. Click a
// row → preview + activate. Click "restore" → the live Y.Doc's shared text is
// overwritten with the historical content under a single transact() (a fresh
// snapshot of the CURRENT state is captured first so the action is undoable
// by picking the new top-of-list row). Restore is content-overwrite, not a
// CRDT rewind — see `restoreSnapshot` in yjs-binding.ts for the rationale.
//
// Default view: snapshots from the LAST 30 DAYS. A "show all" link below the
// list expands to the full log (up to LOG_LIMIT=64 entries per doc).
//
// Storage: backed by Dexie's `yjs_snapshot_log` table (schema v2). Survives
// page reloads; per-doc capped at LOG_LIMIT entries with a tail-trim after
// each idle write.

import {
  createSignal,
  createMemo,
  createResource,
  createEffect,
  Show,
  For,
  onCleanup,
  onMount,
} from "solid-js";
import * as Y from "yjs";
import {
  bindDocToYjs,
  flushDocYjs,
  listSnapshots,
  restoreSnapshot,
  readYDocContent,
  type SnapshotRecord,
  type VaultDoc,
} from "@tether/storage";
import { TetherDoc as TetherDocElement } from "@tether/viewer";
import { diffLines, formatDiffBadge } from "./diff-utils";

export interface EditHistoryViewerProps {
  doc: VaultDoc;
  /** Called after a successful restore so the parent can refresh its draft. */
  onRestored?: () => void;
}

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

/** Decode a Yjs snapshot's binary state into the markdown string. */
function decodeContent(state: Uint8Array): string {
  const fresh = new Y.Doc();
  try {
    Y.applyUpdate(fresh, state);
    return readYDocContent(fresh);
  } finally {
    fresh.destroy();
  }
}

function formatTimestamp(ts: number): string {
  const d = new Date(ts);
  const now = Date.now();
  const delta = now - ts;
  if (delta < 60_000) return "just now";
  if (delta < 3600_000) return `${Math.round(delta / 60_000)}m ago`;
  if (delta < 86_400_000) return `${Math.round(delta / 3600_000)}h ago`;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function EditHistoryViewer(props: EditHistoryViewerProps) {
  const [showAll, setShowAll] = createSignal(false);
  const [activeTs, setActiveTs] = createSignal<number | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [confirmRestore, setConfirmRestore] = createSignal(false);
  const [refreshTick, setRefreshTick] = createSignal(0);

  const [snaps] = createResource(
    () => [props.doc.id, refreshTick()] as const,
    async ([docId]) => {
      // Make sure the doc is bound so the in-memory ring is captured for any
      // edits that happen while the viewer is open.
      await bindDocToYjs(docId, { content: props.doc.content });
      // Force at least one snapshot to exist by flushing the binding's
      // pending idle write. If the binding is brand new this captures the
      // seed content as the first history row; otherwise it's a no-op when
      // there's nothing pending.
      await flushDocYjs(docId);
      return listSnapshots(docId);
    },
  );

  // Auto-refresh on a slow timer so any new idle snapshot lands in the list.
  onMount(() => {
    const t = setInterval(() => setRefreshTick((n) => n + 1), 4000);
    onCleanup(() => clearInterval(t));
  });

  const filtered = createMemo<SnapshotRecord[]>(() => {
    const list = snaps() ?? [];
    if (showAll()) return list;
    const cutoff = Date.now() - THIRTY_DAYS_MS;
    return list.filter((r) => r.ts >= cutoff);
  });

  // Pre-decode each snapshot's content once so diff badges + the preview
  // pane don't re-decode on every render.
  const decoded = createMemo<Map<number, string>>(() => {
    const m = new Map<number, string>();
    for (const r of filtered()) m.set(r.ts, decodeContent(r.state));
    return m;
  });

  const activeContent = createMemo<string | null>(() => {
    const ts = activeTs();
    if (ts == null) return null;
    return decoded().get(ts) ?? null;
  });

  const diffs = createMemo<Map<number, string>>(() => {
    // For each snapshot, compute its line diff against the current live
    // content.
    const liveContent = props.doc.content;
    const m = new Map<number, string>();
    for (const r of filtered()) {
      const snapText = decoded().get(r.ts) ?? "";
      const stats = diffLines(snapText, liveContent);
      m.set(r.ts, formatDiffBadge(stats));
    }
    return m;
  });

  // Auto-select the second-newest snapshot (the newest is identical to live;
  // not super useful as a preview).
  createEffect(() => {
    const list = filtered();
    if (activeTs() == null && list.length >= 2) {
      setActiveTs(list[1]?.ts ?? null);
    } else if (activeTs() == null && list.length === 1) {
      setActiveTs(list[0]?.ts ?? null);
    }
  });

  async function handleRestore(): Promise<void> {
    const ts = activeTs();
    if (ts == null || busy()) return;
    if (!confirmRestore()) {
      setConfirmRestore(true);
      setTimeout(() => setConfirmRestore(false), 2500);
      return;
    }
    setBusy(true);
    try {
      await restoreSnapshot(props.doc.id, ts);
      setRefreshTick((n) => n + 1);
      setConfirmRestore(false);
      props.onRestored?.();
    } finally {
      setBusy(false);
    }
  }

  const totalSnaps = createMemo(() => snaps()?.length ?? 0);
  const filteredCount = createMemo(() => filtered().length);

  return (
    <div class="mv-history" data-testid="edit-history-viewer">
      <div class="mv-history__list">
        <Show
          when={filteredCount() > 0}
          fallback={
            <div class="mv-history__empty">
              No snapshots yet. Edit the doc; one is captured on idle.
            </div>
          }
        >
          <For each={filtered()}>
            {(rec) => (
              <div
                class="mv-history__row"
                data-testid="history-row"
                data-active={rec.ts === activeTs() ? "true" : "false"}
                data-persisted={rec.persisted ? "true" : "false"}
                onClick={() => {
                  setActiveTs(rec.ts);
                  setConfirmRestore(false);
                }}
              >
                <span class="mv-history__when">{formatTimestamp(rec.ts)}</span>
                <span class="mv-history__sub">
                  <span
                    class="mv-history__badge"
                    classList={{
                      "mv-history__badge--added": (diffs().get(rec.ts) ?? "")
                        .startsWith("+"),
                      "mv-history__badge--removed": (diffs().get(rec.ts) ?? "")
                        .startsWith("-"),
                    }}
                  >
                    {diffs().get(rec.ts) ?? "·"}
                  </span>
                  <Show when={rec.persisted}>
                    <span class="mv-history__badge">disk</span>
                  </Show>
                </span>
              </div>
            )}
          </For>
        </Show>
        <Show when={!showAll() && totalSnaps() > filteredCount()}>
          <button
            class="mv-history__expand"
            onClick={() => setShowAll(true)}
            data-testid="history-show-all"
          >
            show all ({totalSnaps()})
          </button>
        </Show>
      </div>

      <div class="mv-history__preview">
        <div class="mv-history__preview-toolbar">
          <span class="mv-history__preview-when">
            <Show
              when={activeTs() != null}
              fallback="select a snapshot to preview"
            >
              {formatTimestamp(activeTs() ?? 0)}
            </Show>
          </span>
          <Show when={activeTs() != null}>
            <button
              class="mv-history__restore"
              data-testid="history-restore"
              data-confirm={confirmRestore() ? "true" : "false"}
              disabled={busy()}
              onClick={() => void handleRestore()}
            >
              {busy()
                ? "restoring…"
                : confirmRestore()
                  ? "click again to restore"
                  : "restore"}
            </button>
          </Show>
        </div>

        <Show
          when={activeContent() != null}
          fallback={<div class="mv-history__empty">no preview</div>}
        >
          <tether-doc
            ref={(el: TetherDocElement) => {
              createEffect(() => {
                const c = activeContent();
                if (c != null && typeof el.setContent === "function") {
                  void el.setContent(c);
                }
              });
            }}
            class="mv-history__doc"
            theme="dark"
            base="/n"
          />
        </Show>
      </div>
    </div>
  );
}
